import { useState } from "react";
import type { AdvisorMovement } from "@/types";
import { MOVEMENT_META } from "@/pages/admin/consignaciones/components/movementMeta";
import { formatCOP, formatDate, formatNumber } from "@/utils/format";

interface MovementHistoryProps {
  movements: AdvisorMovement[];
  onDelete: (id: string) => void;
}

const MovementHistory = ({ movements, onDelete }: MovementHistoryProps) => {
  const [confirmId, setConfirmId] = useState<string | null>(null);

  if (movements.length === 0) {
    return (
      <div className="glass-panel rounded-lg px-5 py-10 text-center">
        <span className="mx-auto w-12 h-12 rounded-lg bg-background-200/50 text-accent-200 flex items-center justify-center mb-3">
          <i className="ri-history-line text-2xl" />
        </span>
        <p className="text-sm text-foreground-700 font-semibold">Sin movimientos registrados</p>
        <p className="text-xs text-foreground-500 mt-1">
          Usa "Entregar / Vender" para mover mercancía hacia este asesor.
        </p>
      </div>
    );
  }

  return (
    <div className="glass-panel rounded-lg overflow-hidden">
      <ul className="divide-y divide-background-300/25">
        {movements.map((m) => {
          const meta = MOVEMENT_META[m.kind];
          return (
            <li key={m.id} className="px-4 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <span className={`w-8 h-8 shrink-0 rounded-md flex items-center justify-center ${meta.badge}`}>
                  <i className={`${meta.icon}`} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground-950 break-words">
                    {m.productName || meta.label}
                  </p>
                  <p className="text-[10px] text-foreground-500 mt-0.5">
                    {meta.label} · {formatDate(m.date)}
                    {m.qty > 0 ? ` · ${formatNumber(m.qty)} und` : ""}
                    {m.note ? ` · ${m.note}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-bold text-foreground-950 whitespace-nowrap">
                  {m.amount > 0 ? formatCOP(m.amount) : "—"}
                </span>
                {confirmId === m.id ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => {
                        onDelete(m.id);
                        setConfirmId(null);
                      }}
                      className="px-2.5 py-1 rounded-md bg-primary-500 hover:bg-primary-600 text-foreground-950 text-[11px] font-semibold whitespace-nowrap cursor-pointer"
                    >
                      Sí
                    </button>
                    <button
                      onClick={() => setConfirmId(null)}
                      className="px-2 py-1 rounded-md text-foreground-500 hover:text-foreground-800 text-[11px] font-semibold cursor-pointer"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmId(m.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:text-primary-200 hover:bg-background-200/60 cursor-pointer"
                    aria-label="Eliminar movimiento"
                    title="Eliminar movimiento"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MovementHistory;